import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Plus, X, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getTableTypeIcon } from '../../utils/tableUtils';
import { groupByTime, formatRelativeTime } from '../../utils/dateUtils';

interface Dataset {
  id: string;
  name: string;
  updated_at: string;
  table_type?: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasets: Dataset[];
  onNewTask: () => void;
}

interface DatasetGroupProps {
  title: string;
  items: Dataset[];
  startIndex: number;
  selectedIndex: number;
  onSelect: (dataset: Dataset) => void;
  onHover: (index: number) => void;
}

const DatasetGroup: React.FC<DatasetGroupProps> = ({
  title,
  items,
  startIndex,
  selectedIndex,
  onSelect,
  onHover,
}) => {
  if (items.length === 0) return null;

  return (
    <div className="mb-0.5">
      <div className="px-3 py-1">
        <span className="text-2xs text-neutral-400 uppercase tracking-wide">{title}</span>
      </div>
      {items.map((dataset, idx) => {
        const itemIndex = startIndex + idx;
        const isSelected = selectedIndex === itemIndex;
        const Icon = getTableTypeIcon(dataset.table_type);

        return (
          <button
            key={dataset.id}
            onClick={() => onSelect(dataset)}
            onMouseEnter={() => onHover(itemIndex)}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition rounded-md mx-1 ${
              isSelected ? 'bg-neutral-100' : 'hover:bg-neutral-50'
            }`}
            style={{ width: 'calc(100% - 8px)' }}
          >
            <div className="w-5 h-5 rounded bg-neutral-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-3 h-3 text-neutral-400" />
            </div>
            <span className="flex-1 min-w-0 text-13 text-neutral-700 truncate">
              {dataset.name}
            </span>
            <span className="text-2xs text-neutral-400 flex-shrink-0">
              {formatRelativeTime(dataset.updated_at)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  datasets,
  onNewTask,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filteredDatasets = datasets.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const groupedDatasets = groupByTime(filteredDatasets);

  const allItems = useMemo(() => [
    { type: 'action' as const, action: 'new-task' },
    ...groupedDatasets.today.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.yesterday.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.thisWeek.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.older.map(d => ({ type: 'dataset' as const, data: d })),
  ], [groupedDatasets]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter': {
          e.preventDefault();
          const selected = allItems[selectedIndex];
          if (selected?.type === 'action' && selected.action === 'new-task') {
            onNewTask();
            onClose();
          } else if (selected?.type === 'dataset' && selected.data) {
            navigate(`/datasets/${selected.data.id}`);
            onClose();
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, allItems, onClose, onNewTask, navigate]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const handleDatasetClick = (dataset: Dataset) => {
    navigate(`/datasets/${dataset.id}`);
    onClose();
  };

  const getStartIndex = (group: 'today' | 'yesterday' | 'thisWeek' | 'older'): number => {
    let index = 1;
    if (group === 'today') return index;
    index += groupedDatasets.today.length;
    if (group === 'yesterday') return index;
    index += groupedDatasets.yesterday.length;
    if (group === 'thisWeek') return index;
    index += groupedDatasets.thisWeek.length;
    return index;
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[200]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[201] flex items-start justify-center pt-[12vh]" onClick={onClose}>
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ animation: 'modalSlideIn 150ms ease-out' }}
        >
          <style>{`
            @keyframes modalSlideIn {
              from { opacity: 0; transform: scale(0.98) translateY(-10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>

          <div className="px-3 py-2.5 border-b border-neutral-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-7 py-1 text-13 bg-transparent outline-none placeholder:text-neutral-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-600 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[340px] overflow-y-auto py-1.5">
            <div className="px-2">
              <button
                onClick={() => {
                  onNewTask();
                  onClose();
                }}
                onMouseEnter={() => setSelectedIndex(0)}
                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition rounded-md ${
                  selectedIndex === 0 ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                }`}
              >
                <div className="w-5 h-5 rounded bg-neutral-900 flex items-center justify-center">
                  <Plus className="w-3 h-3 text-white" />
                </div>
                <span className="text-13 text-neutral-700">New Task</span>
              </button>
            </div>

            <div className="h-px bg-neutral-100 mx-3 my-1.5" />

            <div className="px-2">
              {filteredDatasets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <Database className="w-6 h-6 text-neutral-300 mb-1.5" />
                  <p className="text-xs text-neutral-400">
                    {searchQuery ? 'No matches' : 'No recent datasets'}
                  </p>
                </div>
              ) : (
                <>
                  <DatasetGroup
                    title="Today"
                    items={groupedDatasets.today}
                    startIndex={getStartIndex('today')}
                    selectedIndex={selectedIndex}
                    onSelect={handleDatasetClick}
                    onHover={setSelectedIndex}
                  />
                  <DatasetGroup
                    title="Yesterday"
                    items={groupedDatasets.yesterday}
                    startIndex={getStartIndex('yesterday')}
                    selectedIndex={selectedIndex}
                    onSelect={handleDatasetClick}
                    onHover={setSelectedIndex}
                  />
                  <DatasetGroup
                    title="This Week"
                    items={groupedDatasets.thisWeek}
                    startIndex={getStartIndex('thisWeek')}
                    selectedIndex={selectedIndex}
                    onSelect={handleDatasetClick}
                    onHover={setSelectedIndex}
                  />
                  <DatasetGroup
                    title="Older"
                    items={groupedDatasets.older}
                    startIndex={getStartIndex('older')}
                    selectedIndex={selectedIndex}
                    onSelect={handleDatasetClick}
                    onHover={setSelectedIndex}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
